import { create } from 'zustand';

/**
 * How the interface is laid out for this browser: the sidebar folded to its
 * icons or not, and whether a list is a table or cards. Kept in localStorage,
 * like the theme — a preference of the person at this screen, not of the
 * account.
 */

export type ListView = 'table' | 'cards';

/** The lists that can be shown either way. */
export type ListScreen = 'sites' | 'templates';

const STORAGE_KEY = 'cp.preferences';

interface Stored {
  readonly sidebarCollapsed: boolean;
  readonly views: Partial<Record<ListScreen, ListView>>;
}

function readStored(): Stored {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    const record = typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
    const views = typeof record.views === 'object' && record.views !== null ? (record.views as Record<string, unknown>) : {};
    return {
      sidebarCollapsed: record.sidebarCollapsed === true,
      views: Object.fromEntries(Object.entries(views).filter(([, view]) => view === 'table' || view === 'cards')) as Stored['views'],
    };
  } catch {
    return { sidebarCollapsed: false, views: {} };
  }
}

function write(state: Stored): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Blocked storage: the choice holds until the page is reloaded.
  }
}

interface PreferencesState extends Stored {
  toggleSidebar(): void;
  setView(screen: ListScreen, view: ListView): void;
}

export const usePreferences = create<PreferencesState>()((set, get) => ({
  ...readStored(),
  toggleSidebar() {
    set({ sidebarCollapsed: !get().sidebarCollapsed });
    write({ sidebarCollapsed: get().sidebarCollapsed, views: get().views });
  },
  setView(screen, view) {
    set({ views: { ...get().views, [screen]: view } });
    write({ sidebarCollapsed: get().sidebarCollapsed, views: get().views });
  },
}));

/** Sites are many and compared by column; templates are few and told apart by their look. */
const DEFAULT_VIEWS: Record<ListScreen, ListView> = { sites: 'table', templates: 'cards' };

/** A list's view, as the person last chose it here. */
export function useListView(screen: ListScreen): [ListView, (view: ListView) => void] {
  const view = usePreferences((state) => state.views[screen] ?? DEFAULT_VIEWS[screen]);
  const setView = usePreferences((state) => state.setView);
  return [view, (next) => setView(screen, next)];
}
